import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const grainAndBloomSkill: CodeoSkill = {
    name: SKILL_NAMES.grainAndBloom,
    kind: 'codeo',
    description:
        'Finishing-pass texture: a fine film-grain overlay plus soft bloom/glow on bright ' +
        'elements and a subtle vignette — the cheap, static treatments that make a flat digital ' +
        'frame read as graded footage.',
    documentUsesSkill: check(
        (_script, markup) => /feTurbulence/.test(markup) || /drop-shadow/.test(markup),
    ),
    instructions: `# Grain & Bloom

This is a *finishing pass*, applied on top of a scene that's already composed. Three treatments, all essentially static (no per-frame animation, so they're free at render time and always seek-safe), that together lift a flat digital frame toward a graded, filmic look:

1. **Film grain** — a fine noise overlay that breaks up clean gradients and flat fills.
2. **Bloom** — a soft glow bleeding off bright elements, as if light is overexposing slightly.
3. **Vignette** — a gentle darkening at the edges that pulls the eye to centre.

## 1. Film grain (SVG feTurbulence)

A single SVG filter generates the noise; one overlay div applies it over the whole canvas.

\`\`\`html
<svg width="0" height="0" style="position:absolute" aria-hidden="true">
    <filter id="grain">
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
    </filter>
</svg>
<div id="grain-overlay"></div>
\`\`\`

\`\`\`css
#grain-overlay {
    position: absolute;
    inset: 0;
    z-index: 9999;               /* above content — it's a lens effect */
    pointer-events: none;
    filter: url(#grain);
    opacity: 0.08;               /* 0.04 subtle · 0.12 gritty — rarely above */
    mix-blend-mode: overlay;
}
\`\`\`

## 2. Bloom (layered glow on bright elements)

For a logo, headline, or any bright focal element, a soft outer glow reads as light:

\`\`\`css
.bloom {
    filter: drop-shadow(0 0 18px rgba(255,255,255,0.35))
            drop-shadow(0 0 48px rgba(122,109,255,0.25));
}
\`\`\`

If you want the bloom to *swell* on a beat (e.g. a stat landing), animate the element's \`filter\` on the single paused timeline — finite, and only briefly:

\`\`\`js
const tl = gsap.timeline({ paused: true });
tl.fromTo('#hero-number',
    { filter: 'drop-shadow(0 0 0px rgba(255,255,255,0))' },
    { filter: 'drop-shadow(0 0 40px rgba(255,255,255,0.6))', duration: 0.4, ease: 'power2.out' }, 0.4);
tl.to('#hero-number', { filter: 'drop-shadow(0 0 18px rgba(255,255,255,0.3))', duration: 0.6, ease: 'power2.inOut' }, 0.8);
window.__timelines = { main: tl };
\`\`\`

## 3. Vignette

\`\`\`html
<div id="vignette"></div>
\`\`\`

\`\`\`css
#vignette {
    position: absolute; inset: 0; z-index: 9998; pointer-events: none;
    background: radial-gradient(circle at 50% 50%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.35) 100%);
}
\`\`\`

## Adapt

- Grain \`baseFrequency\` 0.7–0.95 gives a fine, photographic grain; lower values look blotchy. Bump opacity for retro/gritty, drop it for clean corporate.
- \`mix-blend-mode: overlay\` keeps grain neutral; \`soft-light\` is gentler; \`screen\` lightens. Match to whether the scene is dark or light.
- Bloom colour should echo the light source — white for highlights, an accent tint (\`var(--l5-color-1, #7a6dff)\`) for branded glow.
- Vignette opacity 0.2–0.4. On already-dark scenes, keep it low or skip it.

## Pitfalls

- **Grain and vignette go ABOVE content** (high \`z-index\`, \`pointer-events: none\`) — they're lens effects, not background. An atmosphere field (\`${SKILL_NAMES.atmosphereGradientField}\`) goes *below*; don't confuse the two layers.
- Keep grain static. Animating \`feTurbulence\` (e.g. a \`seed\` tween) is expensive per frame and doesn't seek cleanly — a fixed noise layer is the standard and looks the same. (These are static \`filter\` styles, not CSS \`animation\`/\`transition\`, so they stay seek-safe.)
- Don't stack \`filter: blur\`/\`drop-shadow\` on many large elements at once; filters are the main render cost here. One grain layer, one vignette, bloom only on focal elements.
- Heavy grain over small body text reduces legibility, and can push text under the WCAG AA contrast floor — keep grain light where text is small.
- Grain over a near-black background can look like compression noise; lift the base slightly or lower grain opacity.`,
};
